import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { EmailSenderState, EmailView } from './emailSender.types'

import type { EmailAttachment } from '@v2/domains/communication/models/email.model'

const initialEmailSenderState: EmailSenderState = {
  from: { id: '', email: '' },
  replyTo: { id: '', email: '' },
  subject: '',
  body: '',
  domainSignature: '',
  attachments: [],
  filesAttachments: [],
  isUploadingAttachments: false,
  view: 'create',
}

const emailSenderSlice = createSlice({
  name: 'emailSender',
  initialState: initialEmailSenderState,
  reducers: {
    setView: (state, action: PayloadAction<EmailView>) => {
      state.view = action.payload
    },

    setFrom: (state, action: PayloadAction<{ id: string; email: string }>) => {
      state.from = action.payload
    },

    setReplyTo: (
      state,
      action: PayloadAction<{ id: string; email: string }>,
    ) => {
      state.replyTo = action.payload
    },

    setSubject: (state, action: PayloadAction<string>) => {
      state.subject = action.payload
    },

    setBody: (state, action: PayloadAction<string>) => {
      state.body = action.payload
    },

    setDomainSignature: (state, action: PayloadAction<string>) => {
      state.domainSignature = action.payload
    },

    setIsUploadingAttachments: (state, action: PayloadAction<boolean>) => {
      state.isUploadingAttachments = action.payload
    },

    addAttachments: (
      state,
      action: PayloadAction<{
        files: File[]
        metadata: EmailAttachment[]
      }>,
    ) => {
      state.attachments = [...action.payload.files, ...state.attachments]
      state.filesAttachments = [
        ...action.payload.metadata,
        ...state.filesAttachments,
      ]
    },

    removeAttachment: (state, action: PayloadAction<number>) => {
      const index = action.payload
      state.attachments.splice(index, 1)
      state.filesAttachments.splice(index, 1)
    },

    clearAttachments: (state) => {
      state.attachments = []
      state.filesAttachments = []
    },

    loadEmailForView: (
      state,
      action: PayloadAction<{
        from: { id: string; email: string }
        replyTo: { id: string; email: string }
        subject: string
        body: string
        filesAttachments: EmailAttachment[]
      }>,
    ) => {
      state.from = action.payload.from
      state.replyTo = action.payload.replyTo
      state.subject = action.payload.subject
      state.body = action.payload.body
      state.filesAttachments = action.payload.filesAttachments
      state.view = 'view'
    },

    resetEmailSender: () => {
      return initialEmailSenderState
    },
  },
})

export const {
  setView,
  setFrom,
  setReplyTo,
  setSubject,
  setBody,
  setDomainSignature,
  setIsUploadingAttachments,
  addAttachments,
  removeAttachment,
  clearAttachments,
  loadEmailForView,
  resetEmailSender,
} = emailSenderSlice.actions

export default emailSenderSlice.reducer
